"use client";

import { useEffect, useRef, useState, type ClipboardEvent, type FocusEvent, type PointerEvent as ReactPointerEvent } from "react";
import Image from "next/image";
import {
  formatArticleDate,
  joinAroundDiagram,
  splitAroundDiagram,
  splitArticleBlocks,
  ARTICLE_BLOCK_MARKERS,
  type MethodologyArticle,
  type ArticleBlockKind,
} from "@/lib/methodology/article";
import type { ExampleCompany } from "@/lib/methodology/example";
import type { MethodologyStrategy } from "@/lib/methodology/strategies";
import MethodCompare from "./MethodCompare";
import MethodExample from "./MethodExample";
import MethodDiagram from "./MethodDiagram";
import StrategyExplorer from "./StrategyExplorer";
import styles from "./methodology.module.css";

function blockElements(root: HTMLElement | null): Element[] {
  if (!root) return [];
  return [...root.children].filter((node) => node.tagName !== "BR");
}

type StrategyBlockKind = Exclude<ArticleBlockKind, "comparison" | "example">;
type StrategyGroups = Record<StrategyBlockKind, MethodologyStrategy[]>;

type Props = {
  article: MethodologyArticle;
  strategies: StrategyGroups;
  exampleCompany: ExampleCompany | null;
};

const STRATEGY_BLOCK_LABELS: Record<StrategyBlockKind, string> = {
  strategies: "Bastion single-factor strategies",
  "multi-strategies": "Bastion multi-factor strategies",
};

function ArticleHtml({
  html,
  strategies,
  exampleCompany,
}: {
  html: string;
  strategies: StrategyGroups;
  exampleCompany: ExampleCompany | null;
}) {
  return (
    <>
      {splitArticleBlocks(html).map((segment, index) =>
        segment.type === "html" ? (
          segment.html.trim() ? (
            <div key={index} className={styles.body} dangerouslySetInnerHTML={{ __html: segment.html }} />
          ) : null
        ) : segment.kind === "comparison" ? (
          <MethodCompare key={segment.kind} />
        ) : segment.kind === "example" ? (
          <MethodExample
            key={segment.kind}
            company={exampleCompany}
            single={strategies.strategies}
            multi={strategies["multi-strategies"]}
          />
        ) : (
          <StrategyExplorer
            key={segment.kind}
            strategies={strategies[segment.kind]}
            label={STRATEGY_BLOCK_LABELS[segment.kind]}
            showSteps={segment.kind === "multi-strategies"}
          />
        ),
      )}
    </>
  );
}

export default function MethodologyArticle({ article, strategies, exampleCompany }: Props) {
  const beforeRef = useRef<HTMLDivElement>(null);
  const afterRef = useRef<HTMLDivElement>(null);
  const slotRef = useRef<HTMLDivElement>(null);
  const activeEditor = useRef<HTMLDivElement | null>(null);
  const savedRange = useRef<Range | null>(null);
  const dragCleanup = useRef<(() => void) | null>(null);
  const [current, setCurrent] = useState(article);
  const [draft, setDraft] = useState(article);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [dragging, setDragging] = useState(false);
  const [canMoveUp, setCanMoveUp] = useState(true);
  const [canMoveDown, setCanMoveDown] = useState(true);

  useEffect(() => {
    fetch("/api/auth/check-admin")
      .then((response) => response.json())
      .then((data) => setIsAdmin(!!data.isAdmin))
      .catch(() => setIsAdmin(false));
  }, []);

  const sourceHtml = useRef(current.bodyHtml);
  sourceHtml.current = current.bodyHtml;

  function syncMoveBounds() {
    setCanMoveUp(blockElements(beforeRef.current).length > 0);
    setCanMoveDown(blockElements(afterRef.current).length > 0);
  }

  // Fill the editors once per editing session. Fast Refresh re-runs effects, and refilling would wipe unsaved text.
  const editorsFilled = useRef(false);
  useEffect(() => {
    if (!editing) {
      editorsFilled.current = false;
      return;
    }
    if (editorsFilled.current) return;
    editorsFilled.current = true;
    const [before, after] = splitAroundDiagram(sourceHtml.current);
    if (beforeRef.current) beforeRef.current.innerHTML = before;
    if (afterRef.current) afterRef.current.innerHTML = after;
    activeEditor.current = beforeRef.current;
    setCanMoveUp(blockElements(beforeRef.current).length > 0);
    setCanMoveDown(blockElements(afterRef.current).length > 0);
    beforeRef.current?.focus();
  }, [editing]);

  useEffect(() => () => dragCleanup.current?.(), []);

  function rememberSelection() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    const node = range.commonAncestorContainer;
    const editor = [beforeRef.current, afterRef.current].find((item) => item?.contains(node));
    if (!editor) return;
    activeEditor.current = editor;
    savedRange.current = range.cloneRange();
  }

  function restoreSelection() {
    const selection = window.getSelection();
    if (!selection || !savedRange.current) return;
    selection.removeAllRanges();
    selection.addRange(savedRange.current);
  }

  function runCommand(command: string, value?: string) {
    const editor = activeEditor.current ?? beforeRef.current;
    editor?.focus();
    restoreSelection();
    document.execCommand(command, false, value);
    rememberSelection();
    syncMoveBounds();
  }

  function toggleHeading() {
    const editor = activeEditor.current ?? beforeRef.current;
    editor?.focus();
    restoreSelection();
    const currentBlock = document.queryCommandValue("formatBlock").toLowerCase();
    document.execCommand("formatBlock", false, currentBlock === "h2" ? "p" : "h2");
    rememberSelection();
  }

  function insertBlock(kind: ArticleBlockKind) {
    const editor = activeEditor.current ?? beforeRef.current;
    editor?.focus();
    restoreSelection();
    if (document.queryCommandValue("formatBlock").toLowerCase() === "h2") {
      document.execCommand("formatBlock", false, "p");
    }
    document.execCommand("insertHTML", false, ARTICLE_BLOCK_MARKERS[kind]);
    rememberSelection();
    syncMoveBounds();
  }

  function openLink() {
    rememberSelection();
    const selection = savedRange.current;
    if (!selection || selection.collapsed) {
      setError("Select the text you want to link.");
      return;
    }
    setError("");
    setLinkUrl("");
    setLinkOpen(true);
  }

  function applyLink() {
    const raw = linkUrl.trim();
    if (!raw) return;
    const href = /^https?:\/\/|^mailto:/i.test(raw) ? raw : `https://${raw}`;
    setLinkOpen(false);
    runCommand("createLink", href);
  }

  function startEditing() {
    setDraft(current);
    setError("");
    setLinkOpen(false);
    setEditing(true);
  }

  function cancelEditing() {
    setDraft(current);
    setError("");
    setLinkOpen(false);
    setEditing(false);
  }

  function relocateDiagram(clientY: number) {
    const before = beforeRef.current;
    const after = afterRef.current;
    if (!before || !after) return;
    for (let guard = 0; guard < 40; guard += 1) {
      const lastBefore = blockElements(before).at(-1);
      if (lastBefore) {
        const rect = lastBefore.getBoundingClientRect();
        if (clientY < rect.top + rect.height / 2) {
          after.insertBefore(lastBefore, blockElements(after)[0] ?? null);
          continue;
        }
      }
      const firstAfter = blockElements(after)[0];
      if (firstAfter) {
        const rect = firstAfter.getBoundingClientRect();
        if (clientY > rect.top + rect.height / 2) {
          before.appendChild(firstAfter);
          continue;
        }
      }
      break;
    }
  }

  function moveDiagram(direction: "up" | "down") {
    const before = beforeRef.current;
    const after = afterRef.current;
    if (!before || !after) return;
    if (direction === "up") {
      const lastBefore = blockElements(before).at(-1);
      if (!lastBefore) return;
      after.insertBefore(lastBefore, blockElements(after)[0] ?? null);
    } else {
      const firstAfter = blockElements(after)[0];
      if (!firstAfter) return;
      before.appendChild(firstAfter);
    }
    syncMoveBounds();
    slotRef.current?.scrollIntoView({ block: "nearest" });
  }

  function liftDiagram(clientX: number, clientY: number) {
    const slot = slotRef.current;
    const figure = slot?.querySelector(".mth");
    if (!slot || !figure) return;
    const rect = figure.getBoundingClientRect();
    slot.style.left = `${rect.left}px`;
    slot.style.top = `${rect.top}px`;
    slot.style.width = `${rect.width}px`;
    slot.dataset.dx = String(clientX - rect.left);
    slot.dataset.dy = String(clientY - rect.top);
    slot.classList.add(styles.diagramFloat);
  }

  function followPointer(clientX: number, clientY: number) {
    const slot = slotRef.current;
    if (!slot) return;
    const dx = Number(slot.dataset.dx ?? 0);
    const dy = Number(slot.dataset.dy ?? 0);
    slot.style.left = `${clientX - dx}px`;
    slot.style.top = `${clientY - dy}px`;
  }

  function dropDiagram() {
    const slot = slotRef.current;
    if (!slot) return;
    slot.classList.remove(styles.diagramFloat);
    slot.style.left = "";
    slot.style.top = "";
    slot.style.width = "";
    delete slot.dataset.dx;
    delete slot.dataset.dy;
  }

  function onMovePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    if ((event.target as HTMLElement).closest("button")) return;
    event.preventDefault();
    const pointerId = event.pointerId;
    liftDiagram(event.clientX, event.clientY);
    setDragging(true);
    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      followPointer(ev.clientX, ev.clientY);
      const edge = 72;
      if (ev.clientY < edge) window.scrollBy(0, -14);
      else if (ev.clientY > window.innerHeight - edge) window.scrollBy(0, 14);
      relocateDiagram(ev.clientY);
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      dragCleanup.current = null;
      dropDiagram();
      setDragging(false);
      syncMoveBounds();
    };
    dragCleanup.current = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  function onEditorFocus(event: FocusEvent<HTMLDivElement>) {
    activeEditor.current = event.currentTarget;
  }

  function onEditorPaste(event: ClipboardEvent<HTMLDivElement>) {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  }

  async function save() {
    const bodyHtml = joinAroundDiagram(beforeRef.current?.innerHTML ?? "", afterRef.current?.innerHTML ?? "");
    if (!draft.title.trim()) {
      setError("Add a title before saving.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const response = await fetch("/api/methodology", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kicker: draft.kicker,
          title: draft.title,
          subtitle: draft.subtitle,
          bodyHtml,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        setError(payload.error || "Save failed.");
        return;
      }
      setCurrent(payload.article);
      setDraft(payload.article);
      setEditing(false);
      setLinkOpen(false);
    } catch {
      setError("Save failed.");
    } finally {
      setSaving(false);
    }
  }

  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (!editing) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [editing]);

  useEffect(() => {
    if (!editing) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        void saveRef.current();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [editing]);

  const shown = editing ? draft : current;
  const bodyParts = splitAroundDiagram(current.bodyHtml);

  return (
    <article className={`${styles.post} ${editing ? styles.postEditing : ""} ${dragging ? styles.dragging : ""}`}>
      {editing ? (
        <div className={styles.toolbar} onMouseDown={(event) => event.preventDefault()}>
          <button type="button" className={styles.tool} onClick={() => runCommand("bold")} aria-label="Bold">
            B
          </button>
          <button type="button" className={`${styles.tool} ${styles.toolItalic}`} onClick={() => runCommand("italic")} aria-label="Italic">
            I
          </button>
          <button type="button" className={styles.tool} onClick={openLink}>
            Link
          </button>
          <button type="button" className={styles.tool} onClick={toggleHeading}>
            Heading
          </button>
          <button type="button" className={styles.tool} onClick={() => runCommand("insertUnorderedList")} aria-label="Bullet list">
            List
          </button>
          <button type="button" className={styles.tool} onClick={() => runCommand("insertOrderedList")} aria-label="Numbered list">
            1.
          </button>
          <button type="button" className={styles.tool} onClick={() => insertBlock("strategies")}>
            Single-factor
          </button>
          <button type="button" className={styles.tool} onClick={() => insertBlock("multi-strategies")}>
            Multi-factor
          </button>
          <button type="button" className={styles.tool} onClick={() => insertBlock("comparison")}>
            Comparison
          </button>
          <button type="button" className={styles.tool} onClick={() => insertBlock("example")}>
            Company
          </button>
          <span className={styles.toolbarSpacer} />
          <button type="button" className={styles.cancel} onClick={cancelEditing} disabled={saving}>
            Cancel
          </button>
          <button type="button" className={styles.save} onClick={() => void save()} disabled={saving}>
            {saving ? "Saving" : "Save"}
          </button>
          {linkOpen ? (
            <form
              className={styles.linkForm}
              onSubmit={(event) => {
                event.preventDefault();
                applyLink();
              }}
            >
              <input
                value={linkUrl}
                onChange={(event) => setLinkUrl(event.target.value)}
                placeholder="https://"
                aria-label="Link address"
                autoFocus
              />
              <button type="submit">Add</button>
            </form>
          ) : null}
        </div>
      ) : null}

      {error ? <p className={styles.error}>{error}</p> : null}

      {editing ? (
        <input
          className={styles.kickerInput}
          value={draft.kicker}
          onChange={(event) => setDraft({ ...draft, kicker: event.target.value })}
          aria-label="Section label"
          maxLength={40}
        />
      ) : (
        <p className={styles.kicker}>{shown.kicker}</p>
      )}

      {editing ? (
        <textarea
          className={styles.titleInput}
          value={draft.title}
          onChange={(event) => setDraft({ ...draft, title: event.target.value })}
          aria-label="Title"
          rows={2}
          maxLength={180}
        />
      ) : (
        <h1>{shown.title}</h1>
      )}

      {editing ? (
        <textarea
          className={styles.subtitleInput}
          value={draft.subtitle}
          onChange={(event) => setDraft({ ...draft, subtitle: event.target.value })}
          aria-label="Subtitle"
          rows={2}
          maxLength={240}
        />
      ) : (
        <p className={styles.subtitle}>{shown.subtitle}</p>
      )}

      <div className={styles.byline}>
        <span className={styles.avatar}>
          <Image src="/iconb-mark.png" alt="" width={22} height={22} />
        </span>
        <div>
          <p className={styles.name}>Investment Bastion</p>
          <p className={styles.date}>{formatArticleDate(current.updatedAt)}</p>
        </div>
        {isAdmin && !editing ? (
          <button type="button" className={styles.edit} onClick={startEditing}>
            Edit
          </button>
        ) : null}
      </div>

      {editing ? (
        <div
          ref={beforeRef}
          className={styles.body}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label="Article text before the diagram"
          onFocus={onEditorFocus}
          onMouseUp={rememberSelection}
          onKeyUp={rememberSelection}
          onInput={syncMoveBounds}
          onPaste={onEditorPaste}
        />
      ) : bodyParts[0] ? (
        <ArticleHtml html={bodyParts[0]} strategies={strategies} exampleCompany={exampleCompany} />
      ) : null}

      {dragging ? <div className={styles.dropLine} /> : null}
      <div ref={slotRef} className={dragging ? styles.diagramFloat : undefined}>
        <MethodDiagram
          company={exampleCompany}
          growth={strategies.strategies.find((strategy) => strategy.strategycode === "PureGrowth") ?? null}
          move={editing ? {
            dragging,
            canMoveUp,
            canMoveDown,
            onPointerDown: onMovePointerDown,
            onMoveUp: () => moveDiagram("up"),
            onMoveDown: () => moveDiagram("down"),
          } : undefined}
        />
      </div>

      {editing ? (
        <div
          ref={afterRef}
          className={styles.body}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label="Article text after the diagram"
          onFocus={onEditorFocus}
          onMouseUp={rememberSelection}
          onKeyUp={rememberSelection}
          onInput={syncMoveBounds}
          onPaste={onEditorPaste}
        />
      ) : bodyParts[1] ? (
        <ArticleHtml html={bodyParts[1]} strategies={strategies} exampleCompany={exampleCompany} />
      ) : null}
    </article>
  );
}
